import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateMelhoria, useUpdateMelhoria, useDeleteMelhoria } from "@/hooks/energia/use-melhoria";
import type { RegistoMelhoria } from "../../lib/energia/melhoria-types";
import {
  EMPTY_HEADER,
  EMPTY_ACAO,
  buildMelhoriaPayload,
  toHeaderForm,
  toAcaoForm,
  type HeaderForm,
  type AcaoForm,
} from "./melhoria-form/melhoria-form-state";
import { DeptOrTextInput } from "./melhoria-form/dept-or-text-input";
import { AcaoRow } from "./melhoria-form/acao-row";

interface MelhoriaFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entry?: RegistoMelhoria | null;
  canDelete?: boolean;
  /** When true, only shows the Sugestão fields (simplified for end-users) */
  suggestionMode?: boolean;
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export function MelhoriaFormDialog({
  open,
  onOpenChange,
  entry,
  canDelete = false,
  suggestionMode = false,
}: MelhoriaFormDialogProps) {
  const isEdit = entry != null;
  const [header, setHeader] = useState<HeaderForm>(EMPTY_HEADER);
  const [acoes, setAcoes] = useState<AcaoForm[]>([{ ...EMPTY_ACAO }]);

  const createMutation = useCreateMelhoria();
  const updateMutation = useUpdateMelhoria();
  const deleteMutation = useDeleteMelhoria();
  const isPending = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setHeader(toHeaderForm(entry));
      setAcoes(entry.acoes.length > 0 ? entry.acoes.map(toAcaoForm) : [{ ...EMPTY_ACAO }]);
    } else {
      setHeader({
        ...EMPTY_HEADER,
        tipo_constatacao: suggestionMode ? "Sugestão" : "",
      });
      setAcoes([{ ...EMPTY_ACAO }]);
    }
  }, [open, entry, suggestionMode]);

  function setH<K extends keyof HeaderForm>(key: K, value: HeaderForm[K]) {
    setHeader((p) => ({ ...p, [key]: value }));
  }

  function setAcao(index: number, key: keyof AcaoForm, value: string) {
    setAcoes((prev) => prev.map((a, i) => (i === index ? { ...a, [key]: value } : a)));
  }

  function addAcao() {
    setAcoes((prev) => [...prev, { ...EMPTY_ACAO }]);
  }

  function removeAcao(index: number) {
    setAcoes((prev) => prev.filter((_, i) => i !== index));
  }

  async function handleDelete() {
    if (!entry) return;
    try {
      await deleteMutation.mutateAsync(entry.id);
      toast.success("Registo eliminado");
      onOpenChange(false);
    } catch {
      toast.error("Erro ao eliminar. Tenta novamente.");
    }
  }

  async function handleSave() {
    const payload = buildMelhoriaPayload(header, acoes);

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({ id: entry.id, ...payload });
        toast.success("Registo atualizado");
      } else {
        await createMutation.mutateAsync(payload);
        toast.success("Registo criado");
      }
      onOpenChange(false);
    } catch {
      toast.error("Erro ao guardar. Tenta novamente.");
    }
  }

  const canSave = header.numero.trim() !== "" && header.tipo_constatacao !== "";
  const isSugestao = header.tipo_constatacao === "Sugestão";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto sm:w-auto">
        <DialogHeader>
          <DialogTitle>
            {suggestionMode && !isEdit
              ? "Enviar sugestão"
              : isEdit ? "Editar registo de melhoria" : "Novo registo de melhoria"}
          </DialogTitle>
          <DialogDescription>
            {suggestionMode && !isEdit
              ? "Tens uma ideia para melhorar algo? Partilha — todas as sugestões são analisadas pela equipa."
              : "AFI.12A — Registo de Melhoria Contínua"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* ── Header section ─────────────────────────────────────────── */}
          {!suggestionMode && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="mc-numero">N.º *</Label>
                <Input
                  id="mc-numero"
                  value={header.numero}
                  onChange={(e) => setH("numero", e.target.value)}
                  placeholder="01/2025"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mc-folha">Folha</Label>
                <Select value={header.folha} onValueChange={(v) => setH("folha", v)}>
                  <SelectTrigger id="mc-folha"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NC_OM_Reclam">NC / OM / Reclamação</SelectItem>
                    <SelectItem value="Auditorias">Auditorias</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mc-tipo">Tipo de Constatação *</Label>
                <Select value={header.tipo_constatacao} onValueChange={(v) => setH("tipo_constatacao", v)}>
                  <SelectTrigger id="mc-tipo"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Não Conformidade">Não conformidade</SelectItem>
                    <SelectItem value="Reclamação">Reclamação</SelectItem>
                    <SelectItem value="Ação de Melhoria">Ação de Melhoria</SelectItem>
                    <SelectItem value="Sugestão">Sugestão</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Suggestion mode: titulo only */}
          {suggestionMode && !isEdit && (
            <div className="space-y-1.5">
              <Label htmlFor="mc-numero-sug">Título da sugestão *</Label>
              <Input
                id="mc-numero-sug"
                value={header.numero}
                onChange={(e) => setH("numero", e.target.value)}
                placeholder="Ex: Melhorar iluminação no armazém"
              />
            </div>
          )}

          {/* Data / Origem */}
          {!suggestionMode && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="mc-data">Data da constatação</Label>
                <Input id="mc-data" type="date" value={header.data_constatacao} onChange={(e) => setH("data_constatacao", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mc-origem">Origem</Label>
                <Input id="mc-origem" value={header.origem} onChange={(e) => setH("origem", e.target.value)} placeholder="Interna, Auditoria Interna..." />
              </div>
            </div>
          )}

          {/* Descrição */}
          <div className="space-y-1.5">
            <Label htmlFor="mc-desc">
              {suggestionMode ? "Descreve a tua sugestão *" : "Descrição"}
            </Label>
            <Textarea
              id="mc-desc"
              rows={3}
              value={header.descricao}
              onChange={(e) => setH("descricao", e.target.value)}
              placeholder={suggestionMode
                ? "Descreve a tua ideia com o máximo de detalhe possível — onde, o quê, e como poderia ser melhorado..."
                : "Descreve a constatação..."}
            />
          </div>

          {/* Causas */}
          {!isSugestao && (
            <div className="space-y-1.5">
              <Label htmlFor="mc-causas">Identificação das Causas</Label>
              <Textarea
                id="mc-causas"
                rows={2}
                value={header.identificacao_causas}
                onChange={(e) => setH("identificacao_causas", e.target.value)}
                placeholder="Causas identificadas..."
              />
            </div>
          )}

          {/* ── Actions ──────────────────────────────────────────────────── */}
          {!suggestionMode && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Ações a Implementar</Label>
                <Button type="button" variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={addAcao}>
                  <Plus className="h-3 w-3" /> Adicionar ação
                </Button>
              </div>
              {acoes.map((acao, i) => (
                <AcaoRow
                  key={i}
                  index={i}
                  acao={acao}
                  onChange={(key, value) => setAcao(i, key, value)}
                  onRemove={() => removeAcao(i)}
                  canRemove={acoes.length > 1}
                />
              ))}
            </div>
          )}

          {/* ── Verification ─────────────────────────────────────────────── */}
          {!suggestionMode && (
            <>
              <div className="border-t pt-3 space-y-3">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Verificação da Eficácia</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="mc-prazo-ver">Prazo de verificação</Label>
                    <Input
                      id="mc-prazo-ver"
                      value={header.prazo_verificacao}
                      onChange={(e) => setH("prazo_verificacao", e.target.value)}
                      placeholder="Ex: 2025-12-31"
                    />
                  </div>
                  <DeptOrTextInput
                    label="Resp. Verificação"
                    inputId="mc-resp-ver"
                    textValue={header.responsavel_verificacao}
                    deptId={header.responsavel_verificacao_dept_id}
                    onTextChange={(v) => setH("responsavel_verificacao", v)}
                    onDeptChange={(id, code) => {
                      setH("responsavel_verificacao_dept_id", id);
                      if (code) setH("responsavel_verificacao", code);
                    }}
                    placeholder="Ex: GER"
                  />
                  <div className="space-y-1.5">
                    <Label htmlFor="mc-eficaz">Eficaz?</Label>
                    <Select value={header.eficaz} onValueChange={(v) => setH("eficaz", v)}>
                      <SelectTrigger id="mc-eficaz"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Sim">Sim</SelectItem>
                        <SelectItem value="Não">Não</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="mc-metodo">Método de Verificação</Label>
                  <Textarea id="mc-metodo" rows={2} value={header.metodo_verificacao} onChange={(e) => setH("metodo_verificacao", e.target.value)} placeholder="Método utilizado para verificar a eficácia..." />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="mc-obs">Observações</Label>
                  <Textarea id="mc-obs" rows={2} value={header.observacoes} onChange={(e) => setH("observacoes", e.target.value)} placeholder="Observações adicionais..." />
                </div>
              </div>

              {/* Result */}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="mc-estado">Estado da ação</Label>
                  <Select value={header.estado} onValueChange={(v) => setH("estado", v)}>
                    <SelectTrigger id="mc-estado"><SelectValue placeholder="Selecionar..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fechada">Fechada</SelectItem>
                      <SelectItem value="Em Curso">Em curso</SelectItem>
                      <SelectItem value="Não Eficaz">Não eficaz</SelectItem>
                      <SelectItem value="Transita">Transita</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mc-nc">N.º NC</Label>
                  <Input id="mc-nc" value={header.numero_nc} onChange={(e) => setH("numero_nc", e.target.value)} placeholder="--" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="mc-risco">Gera Risco/Oportunidade?</Label>
                  <Select value={header.gera_risco_oportunidade} onValueChange={(v) => setH("gera_risco_oportunidade", v)}>
                    <SelectTrigger id="mc-risco"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sim">Sim</SelectItem>
                      <SelectItem value="nao">Não</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          <div>
            {isEdit && canDelete && (
              <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleDelete} disabled={isPending}>
                {deleteMutation.isPending ? "A eliminar..." : "Eliminar"}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} disabled={isPending}>
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSave} disabled={isPending || !canSave}>
              {createMutation.isPending || updateMutation.isPending
                ? "A guardar..."
                : suggestionMode && !isEdit ? "Enviar sugestão" : "Guardar"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
